import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { api, bookUrl } from "../lib/api";
import { formatHebrewRef, numberToHebrew } from "../lib/hebrewRef";
import { useAuth } from "../context/AuthContext";
import { useBasket } from "../context/BasketContext";
import { useLearning } from "../context/LearningContext";
import {
  ScrollText,
  Sparkles,
  Highlighter,
  StickyNote,
  BookMarked,
  BookOpen,
  ArrowLeft,
  Clock,
  GraduationCap,
  NotebookPen,
  Share2,
  Heart,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { BasketSheet } from "../components/BasketSheet";
import ShareAppModal from "../components/ShareAppModal";

/**
 * "בית המדרש שלי" — personal Torah study workspace.
 *
 * Single, canonical hub. Per the Feb 2026 cleanup spec the page renders
 * EXACTLY these cards and no others:
 *
 *   1. 📖 המשך לימוד               (continue-learning hero card)
 *   2. 🖍️ הסימונים שלי
 *   3. 📝 ההערות שלי               (links to /beit-midrash/highlights?tab=notes)
 *   4. 🎓 שיעורי התורה שלי         (saved lessons library)
 *   5. ✨ בנה שיעור תורה           (opens the basket → lesson builder flow)
 *   6. ✦ פסוק היום                 (deep link to /daily — kept as a daily-learning aid)
 *
 * REMOVED in this iteration: the standalone "סל שיעור" card (now an internal
 * step inside "בנה שיעור תורה"), the duplicate "הסימניות שלי" card (covered
 * inside the highlights page), the unimplemented "המחברות שלי" and
 * "השאלות שלי" stubs. No dead buttons remain.
 */
export default function BeitMidrash() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const dir = isEn ? "ltr" : "rtl";
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { count: basketCount } = useBasket();
  const learning = useLearning();
  const [data, setData] = useState(null);
  const [openBasket, setOpenBasket] = useState(false);
  const [lessonsCount, setLessonsCount] = useState(0);
  const [notebookCount, setNotebookCount] = useState(null); // null = not yet loaded
  const [openShare, setOpenShare] = useState(false);
  const [referral, setReferral] = useState(null);

  // Pull the user's share/referral stats so we can show a teaser card.
  useEffect(() => {
    if (!user) return;
    api.myReferral().then(setReferral).catch(() => {});
  }, [user]);

  const reloadLessons = useCallback(async () => {
    try {
      const list = await api.listLessons();
      setLessonsCount(Array.isArray(list) ? list.length : 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth?next=/beit-midrash", { replace: true });
      return;
    }
    api.meDashboard().then(setData).catch(() => {});
    reloadLessons();
    // Notebook entry count — used by the Notebook hub card. We only need
    // total length, not the entries themselves, so cap the page size
    // small and respect any backend pagination shape.
    api
      .notebookEntries({ limit: 1 })
      .then((res) => {
        const total =
          (typeof res?.total === "number" && res.total) ||
          (Array.isArray(res?.entries) ? res.entries.length : 0) ||
          (Array.isArray(res) ? res.length : 0);
        setNotebookCount(total);
      })
      .catch(() => setNotebookCount(0));
  }, [user, authLoading, navigate, reloadLessons]);

  if (!user) return null;

  // Counts are sourced LIVE from the learning context so any highlight /
  // note action anywhere in the app reflects here instantly.
  const counts = {
    highlights: learning.highlights.length,
    notes: learning.notes.length,
    lessons: lessonsCount,
  };
  const last = data?.last_reading;
  const recents = data?.recent_reading || [];

  return (
    <div className="relative min-h-[calc(100vh-4rem)]" data-testid="beit-midrash-page" dir={dir}>
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-10 md:pt-14 pb-24">
        {/* Header */}
        <div className="text-center space-y-3 mb-10">
          <div className="inline-flex items-center gap-2 text-gold">
            <BookMarked className="h-4 w-4" strokeWidth={1.5} />
            <span className="font-latin italic text-xs tracking-[0.35em] uppercase">
              {t("beit_midrash.eyebrow")}
            </span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl text-foreground">
            {t("beit_midrash.title")}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground font-heading max-w-xl mx-auto">
            {t("beit_midrash.subtitle")}
          </p>
          <div className="ornamental-divider mx-auto max-w-xs">✦ ❖ ✦</div>
        </div>

        {/* Continue learning */}
        <div className="mb-10">
          <ContinueLearningCard last={last} />
        </div>

        {/* Smart cards — exactly the 5 canonical entries */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
          data-testid="bm-cards"
        >
          <HubCard
            testid="bm-card-highlights"
            icon={<Highlighter className="h-5 w-5" strokeWidth={1.5} />}
            title={t("beit_midrash.card_highlights_title")}
            count={counts.highlights}
            description={t("beit_midrash.card_highlights_desc")}
            ctaLabel={t("beit_midrash.card_highlights_cta")}
            to="/beit-midrash/highlights"
          />
          <HubCard
            testid="bm-card-notes"
            icon={<StickyNote className="h-5 w-5" strokeWidth={1.5} />}
            title={t("beit_midrash.card_notes_title")}
            count={counts.notes}
            description={t("beit_midrash.card_notes_desc")}
            ctaLabel={t("beit_midrash.card_notes_cta")}
            to="/beit-midrash/highlights?tab=notes"
          />
          <HubCard
            testid="bm-card-my-lessons"
            icon={<GraduationCap className="h-5 w-5" strokeWidth={1.5} />}
            title={t("beit_midrash.card_lessons_title")}
            count={counts.lessons}
            description={t("beit_midrash.card_lessons_desc")}
            ctaLabel={t("beit_midrash.card_lessons_cta")}
            to="/beit-midrash/lessons"
          />
          <HubCard
            testid="bm-card-notebook"
            icon={<NotebookPen className="h-5 w-5" strokeWidth={1.5} />}
            title={t("beit_midrash.card_notebook_title")}
            count={notebookCount ?? undefined}
            description={
              notebookCount && notebookCount > 0
                ? t("beit_midrash.card_notebook_desc_continue")
                : t("beit_midrash.card_notebook_desc_empty")
            }
            ctaLabel={notebookCount && notebookCount > 0 ? t("beit_midrash.card_notebook_cta_continue") : t("beit_midrash.card_notebook_cta_empty")}
            to="/notebook"
          />
          <HubCard
            testid="bm-card-build-lesson"
            icon={<Sparkles className="h-5 w-5" strokeWidth={1.5} />}
            title={t("beit_midrash.card_build_title")}
            description={
              basketCount > 0
                ? t("beit_midrash.card_build_desc_full", { count: basketCount })
                : t("beit_midrash.card_build_desc_empty")
            }
            ctaLabel={basketCount > 0 ? t("beit_midrash.card_build_cta_full") : t("beit_midrash.card_build_cta_empty")}
            onCta={() => setOpenBasket(true)}
            highlight={basketCount > 0}
          />
          <HubCard
            testid="bm-card-daily"
            icon={<ScrollText className="h-5 w-5" strokeWidth={1.5} />}
            title={t("beit_midrash.card_daily_title")}
            description={t("beit_midrash.card_daily_desc")}
            ctaLabel={t("beit_midrash.card_daily_cta")}
            to="/daily"
          />
          <HubCard
            testid="bm-card-share"
            icon={<Share2 className="h-5 w-5" strokeWidth={1.5} />}
            title={t("beit_midrash.card_share_title")}
            description={
              referral
                ? t("beit_midrash.card_share_desc_stats", { signups: referral.signups, clicks: referral.clicks })
                : t("beit_midrash.card_share_desc_default")
            }
            ctaLabel={t("beit_midrash.card_share_cta")}
            onCta={() => setOpenShare(true)}
          />
          <HubCard
            testid="bm-card-donate"
            icon={<Heart className="h-5 w-5" strokeWidth={1.5} />}
            title={t("beit_midrash.card_donate_title")}
            description={t("beit_midrash.card_donate_desc")}
            ctaLabel={t("beit_midrash.card_donate_cta")}
            to="/donate"
          />
        </div>

        {/* Recent reading */}
        <div className="mt-12">
          <h3 className="font-heading text-xl text-foreground mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-gold" strokeWidth={1.5} />
            {t("beit_midrash.recents_heading")}
          </h3>
          {recents.length === 0 ? (
            <p
              data-testid="bm-recents-empty"
              className="text-sm text-muted-foreground border border-dashed border-border/60 rounded-sm px-4 py-6 text-center"
            >
              {t("beit_midrash.recents_empty")}
            </p>
          ) : (
            <ul
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
              data-testid="bm-recents"
            >
              {recents.map((r, i) => (
                <li key={i}>
                  <Link
                    to={`/reader/${bookUrl(r.book)}/${r.chapter}`}
                    data-testid={`bm-recent-${r.book}-${r.chapter}`}
                    className="block border border-border/60 rounded-sm p-3 bg-card hover:border-gold transition-colors group"
                  >
                    <span className="block font-heading text-sm text-foreground group-hover:text-gold">
                      {formatHebrewRef(r.book_he || r.book, r.chapter, undefined, "chapter-only")}
                    </span>
                    <span className="block font-latin italic text-[11px] text-muted-foreground mt-0.5">
                      Chapter {r.chapter}
                      {r.verse ? ` · v.${r.verse}` : ""}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <BasketSheet open={openBasket} onClose={() => setOpenBasket(false)} />
      <ShareAppModal open={openShare} onClose={() => setOpenShare(false)} />
    </div>
  );
}

function ContinueLearningCard({ last }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  if (!last) {
    return (
      <Link
        to="/library"
        data-testid="bm-continue-card"
        className="relative block rounded-sm border border-gold/30 bg-card shadow-book p-6 md:p-7 overflow-hidden hover:border-gold/60 transition-all group"
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-transparent via-gold/60 to-transparent" />
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-latin italic text-[11px] tracking-[0.35em] uppercase text-gold">
              {t("beit_midrash.start_eyebrow")}
            </p>
            <h3 className="font-heading text-2xl md:text-3xl text-foreground mt-2">
              {t("beit_midrash.start_title")}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t("beit_midrash.start_subtitle")}
            </p>
          </div>
          <ArrowLeft className={`h-5 w-5 text-gold ${isEn ? "rotate-180" : ""}`} strokeWidth={1.5} />
        </div>
      </Link>
    );
  }
  // Localised continuation: EN uses Sefaria's English name + Arabic numerals
  // and Israeli-locale dates flip to en-US so the muted line below the
  // headline reads naturally for an English-speaking learner.
  const continueTitle = isEn
    ? `${last.book || last.book_he} · ${t("beit_midrash.chapter_label", { n: last.chapter })}`
    : `${last.book_he} · פרק ${numberToHebrew(last.chapter)}`;
  return (
    <Link
      to={`/reader/${bookUrl(last.book)}/${last.chapter}`}
      data-testid="bm-continue-card"
      className="relative block rounded-sm border border-gold/30 bg-card shadow-book p-6 md:p-7 overflow-hidden hover:border-gold/60 transition-all group"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-transparent via-gold/60 to-transparent" />
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-latin italic text-[11px] tracking-[0.35em] uppercase text-gold">
            {t("beit_midrash.continue_eyebrow")}
          </p>
          <h3
            data-testid="bm-continue-title"
            className="font-heading text-2xl md:text-3xl text-foreground mt-2"
          >
            {continueTitle}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {last.book} {last.chapter}
            {last.verse ? `:${last.verse}` : ""} ·{" "}
            {last.updated_at
              ? new Date(last.updated_at).toLocaleString(isEn ? "en-US" : "he-IL", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })
              : ""}
          </p>
        </div>
        <Button className="sacred-btn-gold !py-2" data-testid="bm-continue-cta">
          <BookOpen className="h-4 w-4" strokeWidth={1.5} />
          {t("beit_midrash.continue_btn")}
        </Button>
      </div>
    </Link>
  );
}

function HubCard({
  testid,
  icon,
  title,
  count,
  description,
  ctaLabel,
  onCta,
  to,
  disabled = false,
  disabledHint = "",
  highlight = false,
}) {
  const inner = (
    <div
      data-testid={testid}
      className={`relative rounded-sm border p-5 md:p-6 transition-all flex flex-col gap-3
        ${disabled
          ? "border-border/40 bg-card/40 opacity-70"
          : "border-border/60 bg-card hover:border-gold/60"}
        ${highlight ? "ring-1 ring-gold/40" : ""}
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-gold">{icon}</div>
        {count !== undefined && (
          <span
            data-testid={`${testid}-count`}
            className="inline-flex items-center justify-center min-w-7 h-7 px-2 rounded-full bg-gold/15 text-gold font-heading text-sm border border-gold/30"
          >
            {count ?? 0}
          </span>
        )}
      </div>
      <div>
        <h4 className="font-heading text-lg text-foreground">{title}</h4>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          {disabled && disabledHint ? disabledHint : description}
        </p>
      </div>
      <div className="mt-auto pt-2">
        <span
          data-testid={`${testid}-cta`}
          className={`inline-flex items-center gap-1.5 text-sm font-heading transition-colors ${
            disabled ? "text-muted-foreground" : "text-gold group-hover:text-gold"
          }`}
        >
          {ctaLabel}
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
        </span>
      </div>
    </div>
  );

  if (disabled) {
    return <div className="block">{inner}</div>;
  }
  if (to) {
    return (
      <Link to={to} className="block group">
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onCta}
      className="block group text-right w-full"
    >
      {inner}
    </button>
  );
}
